import type { AudioEngine } from "../AudioEngine";

// ImpactFX: a one-shot sub thump + noise burst for the drop downbeat.
// Pairs with RiserFX -- the riser sweeps up to silence, then the
// impact lands on the drop's first kick.
//
// Anatomy:
//   - Sub: a fast pitch-sweep sine from 80Hz down to 30Hz (longer than
//     the kick's sweep so it has more "weight").
//   - Noise burst: short hi-passed noise for the air-pressure transient.
//   - White noise tail: a long bandpass noise wash at low volume that
//     decays over ~600ms for the post-impact "boom" tail.

export class ImpactFX {
  private engine: AudioEngine;
  private noiseBuffer: AudioBuffer | null = null;

  constructor(engine: AudioEngine) {
    this.engine = engine;
  }

  trigger(time: number, gain = 0.7): void {
    const ctx = this.engine.context;
    const dest = this.engine.musicDestination ?? this.engine.destination;
    if (!ctx || !dest) return;
    if (!this.noiseBuffer) this.noiseBuffer = makeNoise(ctx, 1.0);

    // Sub thump
    const sub = ctx.createOscillator();
    sub.type = "sine";
    sub.frequency.setValueAtTime(80, time);
    sub.frequency.exponentialRampToValueAtTime(30, time + 0.35);
    const subGain = ctx.createGain();
    subGain.gain.setValueAtTime(gain, time);
    subGain.gain.exponentialRampToValueAtTime(0.0001, time + 0.55);
    sub.connect(subGain).connect(dest);
    sub.start(time);
    sub.stop(time + 0.6);

    // Bright noise transient -- "air punch"
    const burst = ctx.createBufferSource();
    burst.buffer = this.noiseBuffer;
    const burstHP = ctx.createBiquadFilter();
    burstHP.type = "highpass";
    burstHP.frequency.value = 800;
    const burstGain = ctx.createGain();
    burstGain.gain.setValueAtTime(gain * 0.5, time);
    burstGain.gain.exponentialRampToValueAtTime(0.0001, time + 0.18);
    burst.connect(burstHP).connect(burstGain).connect(dest);
    burst.start(time);
    burst.stop(time + 0.2);

    // Long noise wash for the room "boom" tail
    const wash = ctx.createBufferSource();
    wash.buffer = this.noiseBuffer;
    const washBP = ctx.createBiquadFilter();
    washBP.type = "bandpass";
    washBP.frequency.value = 320;
    washBP.Q.value = 0.7;
    const washGain = ctx.createGain();
    washGain.gain.setValueAtTime(gain * 0.18, time);
    washGain.gain.exponentialRampToValueAtTime(0.0001, time + 0.7);
    wash.connect(washBP).connect(washGain).connect(dest);
    wash.start(time);
    wash.stop(time + 0.75);
  }
}

function makeNoise(ctx: AudioContext, durationSec: number): AudioBuffer {
  const length = Math.floor(ctx.sampleRate * durationSec);
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
  return buffer;
}
